import { useState, useRef, useEffect, useCallback } from "react";
import ReactMarkdown from "react-markdown";
import {
  Sparkles,
  Plus,
  ArrowUp,
  Loader2,
  Check,
  Film,
  Layers,
  LayoutGrid,
  Youtube,
  Upload,
  Boxes,
  Zap,
  ChevronDown,
  X,
  Download,
  Maximize2,
  SquarePen,
} from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useAgencyClients } from "@/hooks/useAgencyClients";
import { useToast } from "@/hooks/use-toast";
import { useRotatingMessage } from "@/components/ui/ai-generating-overlay";
import { ReferenceImagesDialog, type SelectedRef } from "@/components/reference-images-dialog";

/**
 * VP-105 ViralBooster — single-view organic agent (Higgsfield-style).
 * Free text → inline clarify card → approval gate → inline generated result.
 * Empty state centers the command bar; once a conversation starts it docks to the bottom.
 */

const ASK_BEFORE_GEN_KEY = "viralpartners.viralbooster.askBeforeGen.v1";
const MODEL_KEY = "viralpartners.viralbooster.model.v1";

// Generation model options (orchestration stays on Gemini). Real OpenRouter ids
// verified against the OpenRouter model catalog.
interface ModelOption {
  id: string;
  name: string;
  desc: string;
}
const MODEL_GROUPS: { provider: string; models: ModelOption[] }[] = [
  {
    provider: "Claude",
    models: [
      {
        id: "anthropic/claude-opus-4.8",
        name: "Claude Opus 4.8",
        desc: "Najmocniejszy, najnowszy",
      },
      { id: "anthropic/claude-opus-4.6", name: "Claude Opus 4.6", desc: "Mocny, kreatywny" },
      { id: "anthropic/claude-sonnet-4.6", name: "Claude Sonnet 4.6", desc: "Szybki, codzienny" },
    ],
  },
  {
    provider: "Google",
    models: [
      { id: "google/gemini-3.1-pro-preview", name: "Gemini 3.1 Pro", desc: "Mocny, wszechstronny" },
      { id: "google/gemini-3.5-flash", name: "Gemini 3.5 Flash", desc: "Najszybszy, lekki" },
    ],
  },
  {
    provider: "OpenAI",
    models: [
      { id: "openai/gpt-5.5-pro", name: "GPT-5.5 Pro", desc: "Topowy OpenAI" },
      { id: "openai/gpt-5.5", name: "GPT-5.5", desc: "Szybki, uniwersalny" },
    ],
  },
  {
    provider: "xAI",
    models: [{ id: "x-ai/grok-4.3", name: "Grok 4.3", desc: "Świeży, kreatywny" }],
  },
];
const ALL_MODELS = MODEL_GROUPS.flatMap((g) => g.models);
const DEFAULT_MODEL = "anthropic/claude-opus-4.8";
const isKnownModel = (id: string | null): id is string =>
  !!id && ALL_MODELS.some((m) => m.id === id);

interface FlowStep {
  i: number;
  kind: "skill" | "vision" | "enhanced-prompt" | "generation" | "artifact" | "note" | "error";
  skillId?: string;
  label: string;
  status?: "running" | "done" | "failed";
  artifactUrl?: string;
  artifactType?: "image" | "video";
  text?: string;
}

interface VbQuestion {
  id: string;
  label: string;
  type: "choice" | "text" | "upload";
  options?: string[];
}
type VbAction =
  | { action: "ask"; message?: string; questions: VbQuestion[] }
  | {
      action: "generate";
      formatId: string;
      summary: string;
      brief: string;
      aspect?: string;
      resolution?: string;
      productUrl?: string;
    }
  | { action: "reply"; message: string };

interface Turn {
  id: string;
  role: "user" | "assistant";
  kind: "text" | "ask" | "approval" | "result";
  text?: string;
  questions?: VbQuestion[];
  formatId?: string;
  brief?: string;
  summary?: string;
  images?: string[];
  videoUrl?: string;
  aspect?: string;
  resolution?: string;
  productUrl?: string;
  answered?: boolean;
  resolved?: boolean;
}

// Curated image models surfaced on the approval card (engine ids).
interface ImageModelOption {
  id: string;
  name: string;
  desc: string;
}
const IMAGE_MODELS: ImageModelOption[] = [
  { id: "nano-banana-pro", name: "Nano Banana Pro", desc: "Produkt + edycja z referencją" },
  { id: "gpt-image-2", name: "GPT Image 2", desc: "Najlepszy tekst na grafice" },
  { id: "seedream-4.5", name: "Seedream 4.5", desc: "Kinowy, realistyczny" },
  { id: "flux-pro-1.1", name: "Flux Pro 1.1", desc: "Artystyczny" },
];
const ASPECT_OPTIONS = ["1:1", "9:16", "16:9", "4:5"] as const;
const RESOLUTION_OPTIONS = ["1K", "2K", "4K"] as const;
// Video (UGC) approval-card pickers — must mirror ugc-studio's supported values.
const VIDEO_ASPECT_OPTIONS = ["9:16", "1:1", "16:9", "4:5"] as const;
const VIDEO_LENGTH_OPTIONS = [5, 10, 15] as const; // Seedance single-shot cap is 15s
interface VideoFormatOption {
  id: string;
  name: string;
}
const VIDEO_FORMATS: VideoFormatOption[] = [
  { id: "ugc", name: "UGC talking-head" },
  { id: "product-review", name: "Recenzja produktu" },
  { id: "unboxing", name: "Unboxing" },
  { id: "tv-spot", name: "Spot TV" },
];

const SUGGESTIONS = [
  { icon: <Film className="w-3.5 h-3.5" />, label: "Reels", prompt: "Zrób scenariusz Reels o " },
  {
    icon: <Layers className="w-3.5 h-3.5" />,
    label: "Stories",
    prompt: "Zrób sekwencję Stories o ",
  },
  {
    icon: <LayoutGrid className="w-3.5 h-3.5" />,
    label: "Karuzela",
    prompt: "Zrób karuzelę edukacyjną o ",
  },
  {
    icon: <Youtube className="w-3.5 h-3.5" />,
    label: "YouTube",
    prompt: "Napisz skrypt na YouTube o ",
  },
];

let turnCounter = 0;
const nextId = () => `vb-${++turnCounter}`;

export default function ViralBoosterChat(): JSX.Element {
  const { toast } = useToast();
  const { selectedClientId } = useAgencyClients();
  const [input, setInput] = useState("");
  const [turns, setTurns] = useState<Turn[]>([]);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [plusOpen, setPlusOpen] = useState(false);
  const [modelOpen, setModelOpen] = useState(false);
  const [model, setModel] = useState<string>(() => {
    if (typeof window === "undefined") return DEFAULT_MODEL;
    const stored = window.localStorage.getItem(MODEL_KEY);
    return isKnownModel(stored) ? stored : DEFAULT_MODEL;
  });
  const [askBeforeGen, setAskBeforeGen] = useState<boolean>(() => {
    if (typeof window === "undefined") return true;
    return window.localStorage.getItem(ASK_BEFORE_GEN_KEY) !== "false";
  });
  // Reference images (public URLs from the shared brand-assets library) attached via the
  // clarify "upload" question — carried across turns until a generation consumes them, then cleared.
  const [refImages, setRefImages] = useState<string[]>([]);
  const [refDialogOpen, setRefDialogOpen] = useState(false);
  // Right-side generation panel (Higgsfield-style studio rail) — opens for image generations.
  const [genPanel, setGenPanel] = useState<{
    open: boolean;
    status: "generating" | "done" | "error";
    images: string[];
    note?: string;
    videoUrl?: string;
    avatarUrl?: string;
  }>({ open: false, status: "generating", images: [] });
  // UGC-flow agent step timeline (Higgsfield-style) — populated from the polled job.
  const [flowSteps, setFlowSteps] = useState<FlowStep[]>([]);
  // Pending step awaiting user approval (when "Zapytaj przed generowaniem" is on).
  const [flowPending, setFlowPending] = useState<{
    skill: string;
    label: string;
    costTokens: number;
    editable?: { clipPrompt: string; monolog: string };
  } | null>(null);
  // Editable clip prompt + PL spoken text at the clip approval gate.
  const [flowEdit, setFlowEdit] = useState<{ clipPrompt: string; monolog: string } | null>(null);
  // Fullscreen media preview (avatar / storyboard / video) with download.
  const [lightbox, setLightbox] = useState<{ url: string; type: "image" | "video" } | null>(null);
  // Background-job polling: graphic generation runs server-side; we poll by id and the work
  // survives navigation/unmount (the job lives in the DB, not in this request/component).
  const [activeJobId, setActiveJobId] = useState<string | null>(null);
  // UGC video runs through the ugc-studio pipeline (separate job table + status enum),
  // polled at /api/ugc-studio/jobs/:id. Tracked independently from the graphic job above.
  const [activeVideoJobId, setActiveVideoJobId] = useState<string | null>(null);
  const qc = useQueryClient();
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [turns, loading, flowSteps]);
  useEffect(() => {
    if (typeof window !== "undefined")
      window.localStorage.setItem(ASK_BEFORE_GEN_KEY, String(askBeforeGen));
  }, [askBeforeGen]);
  useEffect(() => {
    if (typeof window !== "undefined") window.localStorage.setItem(MODEL_KEY, model);
  }, [model]);

  const modelName = ALL_MODELS.find((m) => m.id === model)?.name ?? "Claude Opus 4.8";
  const rotatingMsg = useRotatingMessage(
    "viralbooster",
    loading || !!activeJobId || !!activeVideoJobId
  );

  // Poll the active background generation job until it reaches a terminal state.
  const { data: jobData } = useQuery<{
    status: string;
    resultUrls?: string[];
    errorMessage?: string | null;
  }>({
    queryKey: ["job", activeJobId],
    queryFn: () => apiRequest("GET", `/api/jobs/${activeJobId}`),
    enabled: !!activeJobId,
    refetchInterval: (q) => {
      const s = (q.state.data as { status?: string } | undefined)?.status;
      return s === "done" || s === "error" ? false : 2500;
    },
  });
  useEffect(() => {
    if (!jobData || !activeJobId) return;
    if (jobData.status === "done") {
      setGenPanel({ open: true, status: "done", images: jobData.resultUrls ?? [] });
      setTurns((t) => [
        ...t,
        { id: nextId(), role: "assistant", kind: "result", text: "Gotowe — grafika po prawej. ✨" },
      ]);
      setActiveJobId(null);
      void qc.invalidateQueries({ queryKey: ["jobs", "active"] });
      void qc.invalidateQueries({ queryKey: ["/api/graphics"] });
    } else if (jobData.status === "error") {
      setGenPanel((p) => ({
        ...p,
        open: true,
        status: "error",
        images: [],
        note: jobData.errorMessage ?? "Nie udało się wygenerować",
      }));
      setActiveJobId(null);
    }
  }, [jobData, activeJobId, qc]);

  // Poll the active UGC-flow agent job until done/error — drives the step timeline.
  const { data: videoJobData } = useQuery<{
    status: string;
    steps?: FlowStep[];
    pending?: {
      skill: string;
      label: string;
      costTokens: number;
      editable?: { clipPrompt: string; monolog: string };
    } | null;
    videoUrl?: string | null;
    errorMessage?: string | null;
  }>({
    queryKey: ["ugc-flow-job", activeVideoJobId],
    queryFn: () => apiRequest("GET", `/api/jobs/${activeVideoJobId}`),
    enabled: !!activeVideoJobId,
    refetchInterval: (q) => {
      const s = (q.state.data as { status?: string } | undefined)?.status;
      return s === "done" || s === "error" ? false : 2500;
    },
  });
  useEffect(() => {
    if (!videoJobData || !activeVideoJobId) return;
    if (videoJobData.steps) setFlowSteps(videoJobData.steps);
    const pend =
      videoJobData.status === "awaiting" && videoJobData.pending ? videoJobData.pending : null;
    setFlowPending(pend);
    if (pend?.editable) setFlowEdit((prev) => prev ?? pend.editable!);
    else if (!pend) setFlowEdit(null);
    if (videoJobData.status === "done") {
      const url = videoJobData.videoUrl ?? undefined;
      if (url)
        setTurns((t) => [
          ...t,
          {
            id: nextId(),
            role: "assistant",
            kind: "result",
            text: "Gotowe — Twoje video UGC. 🎬",
            formatId: "video",
            videoUrl: url,
          },
        ]);
      setActiveVideoJobId(null);
    } else if (videoJobData.status === "error") {
      setActiveVideoJobId(null);
    }
  }, [videoJobData, activeVideoJobId, qc]);

  const resumeFlow = useCallback(() => {
    if (!activeVideoJobId) return;
    const body = flowEdit ? { clipPrompt: flowEdit.clipPrompt, monolog: flowEdit.monolog } : {};
    setFlowPending(null);
    setFlowEdit(null);
    void apiRequest("POST", `/api/viralbooster/ugc-flow/${activeVideoJobId}/resume`, body).catch(
      () => {
        /* poller will recover */
      }
    );
  }, [activeVideoJobId, flowEdit]);

  // Restore the session on mount: reattach a graphic job (?job=) AND — crucially —
  // rebuild the conversation + reattach any in-flight UGC-flow agent from ?conversationId.
  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    const jobParam = sp.get("job");
    if (jobParam) {
      setActiveJobId(jobParam);
      setGenPanel({ open: true, status: "generating", images: [] });
    }
    const convParam = sp.get("conversationId");
    if (!convParam) return;
    setConversationId(convParam);
    void (async () => {
      try {
        const msgs = (await apiRequest(
          "GET",
          `/api/conversations/${convParam}/messages`
        )) as Array<{ id: string; role: string; content: string }>;
        if (Array.isArray(msgs) && msgs.length) {
          setTurns(
            msgs
              .filter((m) => m.content?.trim())
              .map((m) => ({
                id: nextId(),
                role: m.role === "user" ? "user" : "assistant",
                kind: "text" as const,
                text: m.content,
              }))
          );
        }
      } catch {
        /* no messages yet */
      }
      try {
        const active = (await apiRequest("GET", "/api/jobs/active")) as {
          jobs?: Array<{ id: string; type: string; deepLink?: string }>;
        };
        const flowJob = (active.jobs ?? []).find(
          (j) => j.type === "ugc-flow" && (j.deepLink ?? "").includes(convParam)
        );
        if (flowJob) {
          setFlowSteps([]);
          setActiveVideoJobId(flowJob.id);
        }
      } catch {
        /* no active job */
      }
    })();
  }, []);

  const post = useCallback(
    async (body: Record<string, unknown>): Promise<void> => {
      setLoading(true);
      try {
        const data = (await apiRequest(
          "POST",
          "/api/viralbooster/messages",
          {
            ...body,
            conversationId,
            clientId: selectedClientId !== "none" ? selectedClientId : undefined,
            askBeforeGeneration: askBeforeGen,
            model,
            referenceImages: refImages.length ? refImages : undefined,
          },
          { timeoutMs: 600_000 }
        )) as {
          type: string;
          jobKind?: string;
          conversationId: string;
          action?: VbAction;
          assistantMessage?: { content: string };
          formatId?: string;
          images?: string[];
          jobId?: string;
          avatarUrl?: string;
          deepLink?: string;
        };
        if (data.conversationId) setConversationId(data.conversationId);

        if (data.type === "job" && data.jobId && data.jobKind === "ugc-flow") {
          // UGC-flow skill agent — render the step timeline INLINE in the thread.
          setRefImages([]);
          setFlowSteps([]);
          setActiveVideoJobId(data.jobId);
          if (data.assistantMessage?.content)
            setTurns((t) => [
              ...t,
              {
                id: nextId(),
                role: "assistant",
                kind: "text",
                text: data.assistantMessage!.content,
              },
            ]);
        } else if (data.type === "job" && data.jobId) {
          // Graphic enqueued as a background job — start polling; result lands via the job poller.
          setRefImages([]);
          setActiveJobId(data.jobId);
          setGenPanel({ open: true, status: "generating", images: [] });
          if (data.assistantMessage?.content)
            setTurns((t) => [
              ...t,
              {
                id: nextId(),
                role: "assistant",
                kind: "text",
                text: data.assistantMessage!.content,
              },
            ]);
        } else if (data.type === "result" && (data.assistantMessage || data.images?.length)) {
          setRefImages([]); // references consumed by the generation
          const hasImages = !!data.images?.length;
          if (hasImages) {
            // Image generations land in the right studio panel, not inline.
            setGenPanel({
              open: true,
              status: "done",
              images: data.images!,
              note: data.assistantMessage?.content,
            });
            setTurns((t) => [
              ...t,
              {
                id: nextId(),
                role: "assistant",
                kind: "result",
                text: data.assistantMessage?.content || "Gotowe — grafika po prawej. ✨",
                formatId: data.formatId,
              },
            ]);
          } else {
            setTurns((t) => [
              ...t,
              {
                id: nextId(),
                role: "assistant",
                kind: "result",
                text: data.assistantMessage?.content,
                formatId: data.formatId,
              },
            ]);
          }
        } else if (data.action?.action === "ask") {
          setTurns((t) => [
            ...t,
            {
              id: nextId(),
              role: "assistant",
              kind: "ask",
              text: data.action!.action === "ask" ? data.action!.message : undefined,
              questions: (data.action as Extract<VbAction, { action: "ask" }>).questions,
            },
          ]);
        } else if (data.action?.action === "generate") {
          const a = data.action as Extract<VbAction, { action: "generate" }>;
          setTurns((t) => [
            ...t,
            {
              id: nextId(),
              role: "assistant",
              kind: "approval",
              summary: a.summary,
              formatId: a.formatId,
              brief: a.brief,
              aspect: a.aspect,
              resolution: a.resolution,
              productUrl: a.productUrl,
            },
          ]);
        } else if (data.action?.action === "reply") {
          setTurns((t) => [
            ...t,
            {
              id: nextId(),
              role: "assistant",
              kind: "text",
              text: (data.action as Extract<VbAction, { action: "reply" }>).message,
            },
          ]);
        }
      } catch (err) {
        const isTimeout =
          err instanceof Error &&
          (err.name === "TimeoutError" || /timeout|aborted/i.test(err.message));
        const desc = isTimeout
          ? "Generacja trwała zbyt długo (timeout). Ten model bywa wolny w 2K/4K — spróbuj ponownie lub wybierz szybszy model / niższą rozdzielczość."
          : err instanceof Error
            ? err.message.replace(/^\d+:\s*/, "")
            : "Spróbuj ponownie";
        setGenPanel((p) => (p.status === "generating" ? { ...p, status: "error", note: desc } : p));
        toast({ title: "Błąd ViralBooster", description: desc, variant: "destructive" });
      } finally {
        setLoading(false);
      }
    },
    [conversationId, selectedClientId, askBeforeGen, model, refImages, toast]
  );

  const send = useCallback(
    (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || loading) return;
      setTurns((t) => [...t, { id: nextId(), role: "user", kind: "text", text: trimmed }]);
      setInput("");
      void post({ message: trimmed });
    },
    [loading, post]
  );

  const submitAnswers = useCallback(
    (turn: Turn, answers: Record<string, string>) => {
      // For an upload question the answer lives in refImages (not `answers`) — reflect
      // that the photo IS attached instead of mislabeling it "(pominięto)".
      const composed = (turn.questions ?? [])
        .map((q) => {
          if (q.type === "upload")
            return `${q.label} ${refImages.length ? `[${refImages.length} zdjęcie(a) załączone]` : "(pominięto)"}`;
          return `${q.label} ${answers[q.id] ?? "(pominięto)"}`;
        })
        .join("\n");
      setTurns((t) => t.map((x) => (x.id === turn.id ? { ...x, answered: true } : x)));
      setTurns((t) => [...t, { id: nextId(), role: "user", kind: "text", text: composed }]);
      void post({ message: composed });
    },
    [post, refImages]
  );

  const skipAnswers = useCallback(
    (turn: Turn) => {
      setTurns((t) => t.map((x) => (x.id === turn.id ? { ...x, answered: true } : x)));
      setTurns((t) => [
        ...t,
        {
          id: nextId(),
          role: "user",
          kind: "text",
          text: "Pomiń pytania — wygeneruj z tego, co już wiadomo.",
        },
      ]);
      void post({ message: "Pomiń pytania — użyj dostępnych informacji i przejdź dalej." });
    },
    [post]
  );

  const onRefsConfirmed = useCallback(
    (selected: SelectedRef[]) => setRefImages(selected.map((s) => s.url)),
    []
  );

  const approve = useCallback(
    (
      turn: Turn,
      opts: {
        imageModel?: string;
        aspect?: string;
        resolution?: string;
        format?: string;
        durationSeconds?: number;
      }
    ) => {
      setTurns((t) => t.map((x) => (x.id === turn.id ? { ...x, resolved: true } : x)));
      if (turn.formatId === "graphic")
        setGenPanel({ open: true, status: "generating", images: [] });
      if (turn.formatId === "video") setGenPanel({ open: true, status: "generating", images: [] });
      void post({
        execute: {
          formatId: turn.formatId,
          brief: turn.brief,
          imageModel: opts.imageModel,
          aspect: opts.aspect,
          resolution: opts.resolution,
          format: opts.format,
          durationSeconds: opts.durationSeconds,
          productUrl: turn.productUrl,
        },
        message: "Zatwierdzam — generuj.",
      });
    },
    [post]
  );

  const newChat = useCallback(() => {
    setTurns([]);
    setConversationId(null);
    setFlowSteps([]);
    setFlowPending(null);
    setActiveVideoJobId(null);
    setActiveJobId(null);
    setGenPanel({ open: false, status: "generating", images: [] });
    setRefImages([]);
    setInput("");
    setLightbox(null);
    if (typeof window !== "undefined" && window.location.search) {
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  const hasConversation = turns.length > 0 || genPanel.open || !!activeJobId || !!activeVideoJobId;

  const commandBar = (
    <div className="rounded-2xl border border-white/[0.1] bg-white/[0.03] p-3">
      <textarea
        rows={2}
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            send(input);
          }
        }}
        placeholder="Opisz, co chcesz stworzyć…"
        className="w-full bg-transparent text-white text-sm placeholder:text-white/30 resize-none focus:outline-none px-1"
      />
      <div className="flex items-center justify-between mt-2">
        <div className="flex items-center gap-2 relative">
          <button
            type="button"
            onClick={() => setPlusOpen((v) => !v)}
            className="w-7 h-7 rounded-full bg-white/[0.06] hover:bg-white/[0.12] flex items-center justify-center text-white/70"
            title="Dodaj"
          >
            <Plus className="w-4 h-4" />
          </button>
          {plusOpen && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setPlusOpen(false)} />
              <div className="absolute bottom-9 left-0 z-20 w-44 rounded-xl border border-white/[0.1] bg-[#0a0c10] py-1 shadow-2xl">
                <button
                  className="w-full flex items-center gap-2 px-3 py-2 text-xs text-white/55 cursor-not-allowed"
                  disabled
                >
                  <Upload className="w-3.5 h-3.5" /> Upload{" "}
                  <span className="ml-auto text-[9px] text-white/30">wkrótce</span>
                </button>
                <button
                  className="w-full flex items-center gap-2 px-3 py-2 text-xs text-white/55 cursor-not-allowed"
                  disabled
                >
                  <Boxes className="w-3.5 h-3.5" /> Elements{" "}
                  <span className="ml-auto text-[9px] text-white/30">wkrótce</span>
                </button>
                <button
                  className="w-full flex items-center gap-2 px-3 py-2 text-xs text-white/55 cursor-not-allowed"
                  disabled
                >
                  <Zap className="w-3.5 h-3.5" /> Use Skill{" "}
                  <span className="ml-auto text-[9px] text-white/30">wkrótce</span>
                </button>
              </div>
            </>
          )}
          {/* Model picker */}
          <div className="relative">
            <button
              type="button"
              onClick={() => setModelOpen((v) => !v)}
              className="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg hover:bg-white/[0.06] text-[11px] text-white/70"
            >
              <Sparkles className="w-3.5 h-3.5 text-emerald-400" />{" "}
              <span className="text-white/85">{modelName}</span>{" "}
              <ChevronDown className="w-3 h-3 text-white/40" />
            </button>
            {modelOpen && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setModelOpen(false)} />
                <div className="absolute bottom-9 left-0 z-20 w-64 max-h-80 overflow-y-auto rounded-xl border border-white/[0.1] bg-[#0a0c10] py-2 shadow-2xl">
                  <div className="px-3 pb-2 mb-1 border-b border-white/[0.06] text-[10px] uppercase tracking-wide text-white/40">
                    Model generacji
                  </div>
                  {MODEL_GROUPS.map((group) => (
                    <div key={group.provider} className="px-1 pb-1">
                      <div className="px-2 py-1 text-[10px] uppercase tracking-wide text-white/35">
                        {group.provider}
                      </div>
                      {group.models.map((m) => (
                        <button
                          key={m.id}
                          type="button"
                          onClick={() => {
                            setModel(m.id);
                            setModelOpen(false);
                          }}
                          className={`w-full flex items-start gap-2 px-2 py-1.5 rounded-lg text-left hover:bg-white/[0.05] ${model === m.id ? "bg-white/[0.04]" : ""}`}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="text-xs text-white/90">{m.name}</div>
                            <div className="text-[10px] text-white/40">{m.desc}</div>
                          </div>
                          {model === m.id && (
                            <Check className="w-3.5 h-3.5 text-emerald-400 mt-0.5" />
                          )}
                        </button>
                      ))}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => setAskBeforeGen((v) => !v)}
            className={`text-[11px] px-2.5 py-1 rounded-md border transition-colors ${askBeforeGen ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-200" : "bg-white/[0.05] border-white/[0.1] text-white/50"}`}
            title="Pytaj o zatwierdzenie przed generowaniem"
          >
            {askBeforeGen ? "Zapytaj przed generowaniem" : "Generuj od razu"}
          </button>
          <button
            type="button"
            onClick={() => send(input)}
            disabled={!input.trim() || loading}
            className="w-9 h-9 rounded-full bg-emerald-500 hover:bg-emerald-400 disabled:opacity-40 flex items-center justify-center text-black"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <ArrowUp className="w-4 h-4" />
            )}
          </button>
        </div>
      </div>
    </div>
  );

  const suggestionRow = (
    <div className="flex flex-wrap items-center gap-2 mt-3 justify-center">
      {SUGGESTIONS.map((s) => (
        <button
          key={s.label}
          type="button"
          onClick={() => setInput(s.prompt)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/[0.08] bg-white/[0.02] text-white/70 text-xs hover:bg-white/[0.05] hover:border-white/[0.15]"
        >
          {s.icon}
          {s.label}
        </button>
      ))}
    </div>
  );

  const glow = (
    <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
      <div
        className="absolute w-[700px] h-[700px] bg-emerald-500/20 rounded-full filter blur-[180px]"
        style={{ top: "-12%", left: "8%" }}
      />
      <div
        className="absolute w-[600px] h-[600px] bg-emerald-500/15 rounded-full filter blur-[160px]"
        style={{ bottom: "-8%", right: "6%" }}
      />
      <div
        className="absolute w-[520px] h-[520px] bg-green-400/10 rounded-full filter blur-[150px]"
        style={{ top: "28%", left: "42%" }}
      />
    </div>
  );

  // ── Empty state: everything centered (Higgsfield-style) ──
  if (!hasConversation) {
    return (
      <div className="flex-1 flex flex-col h-full overflow-hidden relative">
        {glow}
        <div className="relative z-10 flex-1 flex flex-col items-center justify-center px-6 -mt-10">
          <div className="text-center mb-8">
            <div className="w-14 h-14 rounded-2xl mx-auto mb-4 bg-gradient-to-br from-emerald-500 to-emerald-700 flex items-center justify-center">
              <Sparkles className="w-7 h-7 text-white" />
            </div>
            <h1 className="text-white text-3xl font-bold tracking-tight">
              Co chcesz dziś stworzyć?
            </h1>
            <p className="text-white/45 text-sm mt-2">
              Opisz pomysł — dopytam o szczegóły i wygeneruję materiał tutaj, w jednym miejscu.
            </p>
          </div>
          <div className="w-full max-w-2xl">
            {commandBar}
            {suggestionRow}
          </div>
        </div>
      </div>
    );
  }

  // ── Conversation: scroll area + docked command bar ──
  return (
    <div className="flex-1 flex h-full overflow-hidden relative">
      {glow}
      {/* Left: conversation column */}
      <div className="flex-1 flex flex-col min-w-0 relative z-10">
        <div className="shrink-0 flex items-center px-4 py-2.5 border-b border-white/[0.06]">
          <button
            type="button"
            onClick={newChat}
            className="inline-flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-white/70 hover:text-white hover:bg-white/[0.06] text-sm font-medium transition-colors"
          >
            <SquarePen className="w-4 h-4" /> Nowy czat
          </button>
        </div>
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-3xl mx-auto px-6 py-8 space-y-5">
            {turns.map((turn) => (
              <TurnView
                key={turn.id}
                turn={turn}
                refImages={refImages}
                onAnswers={submitAnswers}
                onSkip={skipAnswers}
                onOpenRefs={() => setRefDialogOpen(true)}
                onApprove={approve}
                onCancel={(t) =>
                  setTurns((prev) =>
                    prev.map((x) => (x.id === t.id ? { ...x, resolved: true } : x))
                  )
                }
              />
            ))}
            {/* UGC-flow agent: live skill timeline inline in the thread (Higgsfield-style). */}
            {flowSteps.length > 0 && (
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-emerald-700 flex items-center justify-center shrink-0">
                  <Sparkles className="w-4 h-4 text-white" />
                </div>
                <div className="flex-1 min-w-0 rounded-2xl rounded-tl-md border border-white/[0.1] bg-white/[0.04] p-4">
                  <FlowTimeline
                    steps={flowSteps}
                    onArtifact={(url, type) => setLightbox({ url, type })}
                  />
                  {flowPending ? (
                    <div className="mt-2 rounded-xl border border-amber-500/30 bg-amber-500/[0.06] p-3">
                      <p className="text-amber-100/90 text-xs font-medium">{flowPending.label}</p>
                      <p className="text-amber-200/60 text-[11px] mt-0.5">
                        Koszt ~{flowPending.costTokens} tokenów
                      </p>
                      {flowEdit && (
                        <div className="mt-2.5 space-y-2">
                          <div>
                            <label className="text-[10px] uppercase tracking-wide text-white/40 mb-1 block">
                              Prompt klipu (edytuj jeśli chcesz)
                            </label>
                            <textarea
                              value={flowEdit.clipPrompt}
                              onChange={(e) =>
                                setFlowEdit((p) => (p ? { ...p, clipPrompt: e.target.value } : p))
                              }
                              rows={6}
                              className="w-full px-2.5 py-2 bg-[#0a0c10] border border-white/[0.12] rounded-lg text-white/85 text-[11px] font-mono resize-y focus:outline-none focus:border-emerald-500/50"
                            />
                          </div>
                          <div>
                            <label className="text-[10px] uppercase tracking-wide text-white/40 mb-1 block">
                              Tekst mówiony (PL — głos ElevenLabs)
                            </label>
                            <textarea
                              value={flowEdit.monolog}
                              onChange={(e) =>
                                setFlowEdit((p) => (p ? { ...p, monolog: e.target.value } : p))
                              }
                              rows={3}
                              className="w-full px-2.5 py-2 bg-[#0a0c10] border border-white/[0.12] rounded-lg text-white text-[12px] resize-y focus:outline-none focus:border-emerald-500/50"
                              placeholder="Co kreatorka mówi po polsku…"
                            />
                          </div>
                        </div>
                      )}
                      <div className="flex items-center gap-2 mt-2.5">
                        <button
                          type="button"
                          onClick={resumeFlow}
                          className="px-3 py-1.5 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-black text-xs font-medium inline-flex items-center gap-1.5"
                        >
                          <Check className="w-3.5 h-3.5" /> Generuj
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setActiveVideoJobId(null);
                            setFlowPending(null);
                            setFlowEdit(null);
                          }}
                          className="px-3 py-1.5 rounded-lg bg-white/[0.06] hover:bg-white/[0.1] text-white/70 text-xs"
                        >
                          Anuluj
                        </button>
                      </div>
                    </div>
                  ) : (
                    !!activeVideoJobId && (
                      <div className="flex items-center gap-2 text-emerald-300/80 text-xs font-medium animate-pulse mt-1">
                        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Produkcja w toku…
                      </div>
                    )
                  )}
                </div>
              </div>
            )}
            {loading && (
              <div className="flex items-center gap-2 text-emerald-300/80 text-sm font-medium animate-pulse">
                <Loader2 className="w-4 h-4 animate-spin" /> {rotatingMsg}
              </div>
            )}
            <div ref={endRef} />
          </div>
        </div>
        <div className="shrink-0 border-t border-white/[0.06] bg-[#0a0c10]/60 backdrop-blur-xl">
          <div className="max-w-3xl mx-auto px-6 py-4">{commandBar}</div>
        </div>
      </div>
      {/* Right: generation studio panel */}
      {genPanel.open && (
        <GenerationPanel
          panel={genPanel}
          steps={flowSteps}
          message={rotatingMsg}
          onClose={() => setGenPanel((p) => ({ ...p, open: false }))}
        />
      )}
      {/* Right: UGC-flow media strip — all generated media for the session. */}
      {!genPanel.open && flowSteps.some((s) => s.artifactUrl) && (
        <div className="hidden md:flex shrink-0 w-[300px] h-full flex-col border-l border-white/[0.08] bg-[#070a0e]/80 backdrop-blur-xl relative z-10">
          <div className="flex items-center gap-2 px-4 py-3 border-b border-white/[0.06] text-sm font-semibold text-white">
            <Boxes className="w-4 h-4 text-emerald-400" /> Media sesji
          </div>
          <div className="flex-1 overflow-y-auto p-3 space-y-3">
            {flowSteps
              .filter((s) => s.artifactUrl)
              .map((s) => (
                <button
                  key={s.i}
                  type="button"
                  onClick={() =>
                    setLightbox({ url: s.artifactUrl!, type: s.artifactType ?? "image" })
                  }
                  className="block w-full group relative rounded-xl overflow-hidden border border-white/[0.08] bg-black/40"
                >
                  {s.artifactType === "video" ? (
                    <video
                      src={s.artifactUrl}
                      loop
                      muted
                      playsInline
                      className="w-full object-cover pointer-events-none"
                    />
                  ) : (
                    <img
                      src={s.artifactUrl}
                      alt={s.label}
                      className="w-full object-cover"
                      loading="lazy"
                    />
                  )}
                  <span className="absolute bottom-1.5 left-2 right-2 text-[10px] text-white/90 drop-shadow truncate">
                    {s.label}
                  </span>
                  <span className="absolute inset-0 bg-black/0 group-hover:bg-black/20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all">
                    <Maximize2 className="w-4 h-4 text-white" />
                  </span>
                </button>
              ))}
          </div>
        </div>
      )}
      <ReferenceImagesDialog
        open={refDialogOpen}
        onOpenChange={setRefDialogOpen}
        onConfirm={onRefsConfirmed}
      />
      {lightbox && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm flex flex-col"
          onClick={() => setLightbox(null)}
        >
          <div
            className="flex items-center justify-end gap-2 p-4 shrink-0"
            onClick={(e) => e.stopPropagation()}
          >
            <a
              href={lightbox.url}
              download
              target="_blank"
              rel="noreferrer"
              className="px-3 py-1.5 rounded-lg bg-white/[0.1] hover:bg-white/[0.18] text-white text-xs inline-flex items-center gap-1.5"
            >
              <Download className="w-4 h-4" /> Pobierz
            </a>
            <button
              type="button"
              onClick={() => setLightbox(null)}
              className="w-8 h-8 rounded-lg bg-white/[0.1] hover:bg-white/[0.18] text-white flex items-center justify-center"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <div
            className="flex-1 min-h-0 flex items-center justify-center p-6"
            onClick={(e) => e.stopPropagation()}
          >
            {lightbox.type === "image" ? (
              <img
                src={lightbox.url}
                alt="Podgląd"
                className="max-w-full max-h-full object-contain rounded-xl"
              />
            ) : (
              <video
                src={lightbox.url}
                controls
                autoPlay
                loop
                playsInline
                className="max-w-full max-h-full rounded-xl"
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const SKILL_LABEL_PL: Record<string, string> = {
  "product-analyzer": "Analiza produktu",
  "ugc-character": "Tworzę kreatora",
  "ugc-board": "Buduję storyboard",
  "ugc-clip": "Renderuję klip",
  assembly: "Montaż finalny",
};

function FlowTimeline({
  steps,
  onArtifact,
}: {
  steps: FlowStep[];
  onArtifact?: (url: string, type: "image" | "video") => void;
}): JSX.Element {
  const label = (s: FlowStep): string =>
    s.kind === "skill" && s.skillId && SKILL_LABEL_PL[s.skillId]
      ? SKILL_LABEL_PL[s.skillId]
      : s.label;
  const prefix = (s: FlowStep): string => {
    switch (s.kind) {
      case "skill":
        return "Viewing skill";
      case "vision":
        return "";
      case "enhanced-prompt":
        return "";
      case "generation":
        return "";
      case "artifact":
        return "";
      case "error":
        return "";
      default:
        return "";
    }
  };
  return (
    <div className="mb-4 space-y-2.5">
      {steps.map((s) => (
        <div key={s.i} className="text-xs">
          <div className="flex items-center gap-2">
            {s.status === "running" ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin text-emerald-400 shrink-0" />
            ) : s.kind === "error" ? (
              <X className="w-3.5 h-3.5 text-red-400 shrink-0" />
            ) : s.kind === "skill" || s.kind === "generation" ? (
              <Sparkles className="w-3.5 h-3.5 text-emerald-400/80 shrink-0" />
            ) : (
              <Check className="w-3.5 h-3.5 text-emerald-400/70 shrink-0" />
            )}
            <span
              className={`truncate ${s.kind === "error" ? "text-red-300" : s.kind === "note" ? "text-white/55" : "text-white/80"}`}
            >
              {prefix(s) && <span className="text-white/40">{prefix(s)} </span>}
              {label(s)}
            </span>
          </div>
          {s.text && (
            <pre className="mt-1 ml-5 text-[10px] text-white/45 bg-black/30 border border-white/[0.06] rounded-md p-2 max-h-24 overflow-y-auto whitespace-pre-wrap font-sans leading-relaxed">
              {s.text}
            </pre>
          )}
          {s.artifactUrl && s.artifactType === "image" && (
            <button
              type="button"
              onClick={() => onArtifact?.(s.artifactUrl!, "image")}
              className="mt-1.5 ml-5 block group relative"
              title="Otwórz pełny ekran"
            >
              <img
                src={s.artifactUrl}
                alt={s.label}
                className="w-28 rounded-lg border border-white/[0.1] object-cover transition-transform group-hover:scale-[1.02]"
                loading="lazy"
              />
              <span className="absolute inset-0 rounded-lg bg-black/0 group-hover:bg-black/20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all">
                <Maximize2 className="w-4 h-4 text-white" />
              </span>
            </button>
          )}
          {s.artifactUrl && s.artifactType === "video" && (
            <button
              type="button"
              onClick={() => onArtifact?.(s.artifactUrl!, "video")}
              className="mt-1.5 ml-5 block"
            >
              <video
                src={s.artifactUrl}
                loop
                playsInline
                muted
                className="w-36 rounded-lg border border-white/[0.1] pointer-events-none"
              />
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

function GenerationPanel({
  panel,
  steps,
  message,
  onClose,
}: {
  panel: {
    status: "generating" | "done" | "error";
    images: string[];
    note?: string;
    videoUrl?: string;
    avatarUrl?: string;
  };
  steps?: FlowStep[];
  message: string;
  onClose: () => void;
}): JSX.Element {
  return (
    <div className="hidden md:flex shrink-0 w-[420px] h-full flex-col border-l border-white/[0.08] bg-[#070a0e]/80 backdrop-blur-xl relative z-10">
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/[0.06]">
        <div className="flex items-center gap-2 text-sm font-semibold text-white">
          <Sparkles className="w-4 h-4 text-emerald-400" /> Studio
        </div>
        <button type="button" onClick={onClose} className="text-white/40 hover:text-white">
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto p-4">
        {steps && steps.length > 0 && <FlowTimeline steps={steps} />}
        {panel.status === "generating" ? (
          panel.avatarUrl ? (
            // UGC video: show the freshly generated creator while the clip renders.
            <div className="w-full aspect-[9/16] rounded-xl border border-white/[0.08] bg-black/40 relative overflow-hidden flex items-center justify-center">
              <img
                src={panel.avatarUrl}
                alt="Kreator"
                className="w-full h-full object-cover opacity-60"
                loading="lazy"
              />
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-black/30">
                <Loader2 className="w-8 h-8 animate-spin text-emerald-400" />
                <span className="text-emerald-300/90 text-sm font-medium animate-pulse text-center px-4">
                  {message}
                </span>
              </div>
            </div>
          ) : (
            <div className="w-full aspect-square rounded-xl border border-white/[0.08] bg-white/[0.02] flex flex-col items-center justify-center gap-3">
              <Loader2 className="w-8 h-8 animate-spin text-emerald-400" />
              <span className="text-emerald-300/80 text-sm font-medium animate-pulse">
                {message}
              </span>
            </div>
          )
        ) : panel.status === "error" ? (
          <div className="w-full aspect-square rounded-xl border border-red-500/30 bg-red-500/[0.06] flex flex-col items-center justify-center gap-3 p-6 text-center">
            <X className="w-8 h-8 text-red-400/80" />
            <span className="text-red-200/90 text-sm font-medium">Nie udało się wygenerować</span>
            {panel.note && (
              <span className="text-white/50 text-xs leading-relaxed">{panel.note}</span>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {panel.videoUrl && (
              <div className="group relative rounded-xl overflow-hidden border border-white/[0.08] bg-black/40">
                <video
                  src={panel.videoUrl}
                  controls
                  autoPlay
                  loop
                  playsInline
                  className="w-full object-contain rounded-xl"
                />
                <a
                  href={panel.videoUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="absolute top-2 right-2 px-2 py-1 rounded-md bg-black/70 text-white/80 text-[11px] opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/90 inline-flex items-center gap-1"
                >
                  <Download className="w-3 h-3" /> Pobierz
                </a>
              </div>
            )}
            {panel.images.map((url, i) => (
              <div
                key={i}
                className="group relative rounded-xl overflow-hidden border border-white/[0.08] bg-black/40"
              >
                <img
                  src={url}
                  alt={`Grafika ${i + 1}`}
                  className="w-full object-contain"
                  loading="lazy"
                />
                <a
                  href={url}
                  target="_blank"
                  rel="noreferrer"
                  className="absolute top-2 right-2 px-2 py-1 rounded-md bg-black/70 text-white/80 text-[11px] opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/90 inline-flex items-center gap-1"
                >
                  <Download className="w-3 h-3" /> Pobierz
                </a>
              </div>
            ))}
            {panel.note && <p className="text-white/50 text-xs">{panel.note}</p>}
          </div>
        )}
      </div>
    </div>
  );
}

function TurnView({
  turn,
  refImages,
  onAnswers,
  onSkip,
  onOpenRefs,
  onApprove,
  onCancel,
}: {
  turn: Turn;
  refImages: string[];
  onAnswers: (t: Turn, a: Record<string, string>) => void;
  onSkip: (t: Turn) => void;
  onOpenRefs: () => void;
  onApprove: (
    t: Turn,
    opts: {
      imageModel?: string;
      aspect?: string;
      resolution?: string;
      format?: string;
      durationSeconds?: number;
    }
  ) => void;
  onCancel: (t: Turn) => void;
}): JSX.Element {
  const [answers, setAnswers] = useState<Record<string, string>>({});
  // Approval-card editable settings (graphic only) — pre-filled from the orchestrator's suggestion.
  const [imgModel, setImgModel] = useState<string>("nano-banana-pro");
  const [aspect, setAspect] = useState<string>(turn.aspect ?? "1:1");
  const [resolution, setResolution] = useState<string>(turn.resolution ?? "1K");
  const [modelMenuOpen, setModelMenuOpen] = useState(false);
  // Approval-card pickers (video only).
  const [vidFormat, setVidFormat] = useState<string>("ugc");
  const [vidLength, setVidLength] = useState<number>(15);
  const [vidAspect, setVidAspect] = useState<string>(turn.aspect ?? "9:16");
  const refCount = refImages.length;

  if (turn.role === "user") {
    return (
      <div className="flex justify-end">
        <div className="max-w-2xl rounded-2xl rounded-br-md px-4 py-2.5 bg-gradient-to-br from-emerald-600 to-emerald-700 text-white text-sm whitespace-pre-wrap">
          {turn.text}
        </div>
      </div>
    );
  }

  if (turn.kind === "ask") {
    return (
      <div className="rounded-2xl border border-white/[0.08] bg-white/[0.03] p-4">
        {turn.text && <p className="text-white/80 text-sm mb-3">{turn.text}</p>}
        <div className="space-y-3">
          {(turn.questions ?? []).map((q) => (
            <div key={q.id}>
              <p className="text-white/70 text-xs mb-1.5">{q.label}</p>
              {q.type === "choice" && q.options ? (
                <div className="flex flex-wrap gap-1.5">
                  {q.options.map((opt) => (
                    <button
                      key={opt}
                      type="button"
                      disabled={turn.answered}
                      onClick={() => setAnswers((a) => ({ ...a, [q.id]: opt }))}
                      className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${answers[q.id] === opt ? "bg-emerald-500/20 border-emerald-500/50 text-emerald-100" : "bg-white/[0.04] border-white/[0.1] text-white/70 hover:bg-white/[0.08]"}`}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              ) : q.type === "upload" ? (
                <div className="flex items-center gap-2 flex-wrap">
                  {refImages.map((src, i) => (
                    <img
                      key={i}
                      src={src}
                      alt={`Referencja ${i + 1}`}
                      className="w-16 h-16 rounded-lg object-cover border border-white/[0.12]"
                      loading="lazy"
                    />
                  ))}
                  {!turn.answered && (
                    <button
                      type="button"
                      onClick={onOpenRefs}
                      className="w-16 h-16 rounded-lg border border-dashed border-white/[0.2] bg-white/[0.02] flex items-center justify-center cursor-pointer hover:bg-white/[0.05] hover:border-emerald-500/40 text-white/50"
                    >
                      <Plus className="w-5 h-5" />
                    </button>
                  )}
                </div>
              ) : (
                <input
                  type="text"
                  disabled={turn.answered}
                  value={answers[q.id] ?? ""}
                  onChange={(e) => setAnswers((a) => ({ ...a, [q.id]: e.target.value }))}
                  className="w-full px-3 py-1.5 bg-[#0a0c10] border border-white/[0.12] rounded-lg text-white text-xs placeholder:text-white/30 focus:outline-none focus:border-emerald-500/50"
                  placeholder="Twoja odpowiedź…"
                />
              )}
            </div>
          ))}
        </div>
        {!turn.answered && (
          <div className="flex items-center justify-between mt-3">
            <span className="text-[10px] text-white/35">
              {refCount > 0 ? `${refCount} referencja(e) dołączone` : ""}
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => onSkip(turn)}
                className="px-3 py-1.5 rounded-lg bg-white/[0.06] hover:bg-white/[0.1] text-white/60 text-xs"
              >
                Pomiń
              </button>
              <button
                type="button"
                onClick={() => onAnswers(turn, answers)}
                className="px-3 py-1.5 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-black text-xs font-medium"
              >
                Dalej
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  if (turn.kind === "approval") {
    const isGraphic = turn.formatId === "graphic";
    const isVideo = turn.formatId === "video";
    const selModel = IMAGE_MODELS.find((m) => m.id === imgModel) ?? IMAGE_MODELS[0];
    return (
      <div className="rounded-2xl border border-amber-500/30 bg-amber-500/[0.06] p-4">
        <p className="text-white/85 text-sm mb-3">{turn.summary}</p>
        {(isGraphic || isVideo) && turn.brief && (
          <pre className="text-[11px] text-white/65 bg-black/30 border border-white/[0.07] rounded-lg p-3 mb-3 max-h-48 overflow-y-auto whitespace-pre-wrap font-sans leading-relaxed">
            {turn.brief}
          </pre>
        )}
        {isVideo && !turn.resolved && (
          <div className="space-y-2.5 mb-3">
            <div>
              <p className="text-[10px] uppercase tracking-wide text-white/40 mb-1">Format</p>
              <div className="flex flex-wrap gap-1.5">
                {VIDEO_FORMATS.map((f) => (
                  <button
                    key={f.id}
                    type="button"
                    onClick={() => setVidFormat(f.id)}
                    className={`px-2.5 py-1 rounded-full text-[11px] border transition-colors ${vidFormat === f.id ? "bg-emerald-500/20 border-emerald-500/50 text-emerald-100" : "bg-white/[0.04] border-white/[0.1] text-white/70 hover:bg-white/[0.08]"}`}
                  >
                    {f.name}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-4 flex-wrap">
              <div>
                <p className="text-[10px] uppercase tracking-wide text-white/40 mb-1">Długość</p>
                <div className="inline-flex items-center gap-1 rounded-lg border border-white/[0.1] bg-white/[0.02] p-0.5">
                  {VIDEO_LENGTH_OPTIONS.map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setVidLength(s)}
                      className={`px-2 py-0.5 rounded-md text-[11px] ${vidLength === s ? "bg-emerald-500/20 text-emerald-100" : "text-white/55 hover:text-white"}`}
                    >
                      {s}s
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wide text-white/40 mb-1">
                  Format obrazu
                </p>
                <div className="inline-flex items-center gap-1 rounded-lg border border-white/[0.1] bg-white/[0.02] p-0.5">
                  {VIDEO_ASPECT_OPTIONS.map((a) => (
                    <button
                      key={a}
                      type="button"
                      onClick={() => setVidAspect(a)}
                      className={`px-2 py-0.5 rounded-md text-[11px] ${vidAspect === a ? "bg-emerald-500/20 text-emerald-100" : "text-white/55 hover:text-white"}`}
                    >
                      {a}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
        {isGraphic && !turn.resolved && (
          <div className="flex flex-wrap items-center gap-2 mb-3">
            {/* Image model picker */}
            <div className="relative">
              <button
                type="button"
                onClick={() => setModelMenuOpen((v) => !v)}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border border-white/[0.12] bg-white/[0.04] text-[11px] text-white/85 hover:bg-white/[0.08]"
              >
                <Sparkles className="w-3 h-3 text-emerald-400" /> {selModel.name}{" "}
                <ChevronDown className="w-3 h-3 text-white/40" />
              </button>
              {modelMenuOpen && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setModelMenuOpen(false)} />
                  <div className="absolute top-8 left-0 z-20 w-60 rounded-xl border border-white/[0.1] bg-[#0a0c10] py-1.5 shadow-2xl">
                    <div className="px-3 pb-1.5 mb-1 border-b border-white/[0.06] text-[10px] uppercase tracking-wide text-white/40">
                      Model do grafiki
                    </div>
                    {IMAGE_MODELS.map((m) => (
                      <button
                        key={m.id}
                        type="button"
                        onClick={() => {
                          setImgModel(m.id);
                          setModelMenuOpen(false);
                        }}
                        className={`w-full flex items-start gap-2 px-2.5 py-1.5 text-left hover:bg-white/[0.05] ${imgModel === m.id ? "bg-white/[0.04]" : ""}`}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="text-xs text-white/90">{m.name}</div>
                          <div className="text-[10px] text-white/40">{m.desc}</div>
                        </div>
                        {imgModel === m.id && (
                          <Check className="w-3.5 h-3.5 text-emerald-400 mt-0.5" />
                        )}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
            {/* Aspect */}
            <div className="inline-flex items-center gap-1 rounded-lg border border-white/[0.1] bg-white/[0.02] p-0.5">
              {ASPECT_OPTIONS.map((a) => (
                <button
                  key={a}
                  type="button"
                  onClick={() => setAspect(a)}
                  className={`px-2 py-0.5 rounded-md text-[11px] ${aspect === a ? "bg-emerald-500/20 text-emerald-100" : "text-white/55 hover:text-white"}`}
                >
                  {a}
                </button>
              ))}
            </div>
            {/* Resolution */}
            <div className="inline-flex items-center gap-1 rounded-lg border border-white/[0.1] bg-white/[0.02] p-0.5">
              {RESOLUTION_OPTIONS.map((r) => (
                <button
                  key={r}
                  type="button"
                  onClick={() => setResolution(r)}
                  className={`px-2 py-0.5 rounded-md text-[11px] ${resolution === r ? "bg-emerald-500/20 text-emerald-100" : "text-white/55 hover:text-white"}`}
                >
                  {r}
                </button>
              ))}
            </div>
          </div>
        )}
        {!turn.resolved ? (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() =>
                onApprove(
                  turn,
                  isGraphic
                    ? { imageModel: imgModel, aspect, resolution }
                    : isVideo
                      ? { format: vidFormat, durationSeconds: vidLength, aspect: vidAspect }
                      : {}
                )
              }
              className="px-3 py-1.5 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-black text-xs font-medium inline-flex items-center gap-1.5"
            >
              <Check className="w-3.5 h-3.5" /> Generuj
            </button>
            <button
              type="button"
              onClick={() => onCancel(turn)}
              className="px-3 py-1.5 rounded-lg bg-white/[0.06] hover:bg-white/[0.1] text-white/70 text-xs"
            >
              Anuluj
            </button>
          </div>
        ) : (
          <p className="text-white/40 text-xs">Zatwierdzono.</p>
        )}
      </div>
    );
  }

  if (turn.kind === "result") {
    return (
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-emerald-700 flex items-center justify-center shrink-0">
          <Sparkles className="w-4 h-4 text-white" />
        </div>
        <div className="flex-1 min-w-0 rounded-2xl rounded-tl-md border border-white/[0.1] bg-white/[0.04] p-4 text-white text-sm leading-relaxed prose-invert">
          {turn.videoUrl && (
            <div className="mb-3">
              <video
                src={turn.videoUrl}
                controls
                loop
                playsInline
                className="w-56 rounded-lg border border-white/[0.1]"
              />
            </div>
          )}
          {turn.images && turn.images.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-3">
              {turn.images.map((url, i) => (
                <a key={i} href={url} target="_blank" rel="noreferrer" className="block">
                  <img
                    src={url}
                    alt={`Grafika ${i + 1}`}
                    className="w-40 rounded-lg border border-white/[0.1] object-cover"
                    loading="lazy"
                  />
                </a>
              ))}
            </div>
          )}
          {turn.text && (
            <ReactMarkdown
              components={{
                p: ({ children }) => (
                  <p className="mb-2 leading-relaxed whitespace-pre-wrap">{children}</p>
                ),
                strong: ({ children }) => (
                  <strong className="font-semibold text-white">{children}</strong>
                ),
                code: ({ children }) => (
                  <code className="bg-white/[0.08] rounded px-1 py-0.5 text-xs">{children}</code>
                ),
                pre: ({ children }) => (
                  <pre className="bg-black/30 border border-white/[0.08] rounded-lg p-3 my-2 text-xs overflow-x-auto whitespace-pre-wrap">
                    {children}
                  </pre>
                ),
              }}
            >
              {turn.text}
            </ReactMarkdown>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-start gap-3">
      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-emerald-700 flex items-center justify-center shrink-0">
        <Sparkles className="w-4 h-4 text-white" />
      </div>
      <div className="flex-1 rounded-2xl rounded-tl-md border border-white/[0.1] bg-white/[0.04] p-4 text-white/85 text-sm whitespace-pre-wrap">
        {turn.text}
      </div>
    </div>
  );
}
